import { inspectionDB } from '../../data/database.structure';
import { inspectionRecordService } from '../inspection/inspectionRecordService';
import { inspectionPushService } from './inspectionPushService';
import { photoDeliveryService } from './photoDeliveryService';
import { SyncDrainService } from './syncDrainService';
import { store } from '../../store';
import { networkStatusAtom } from '../../store/network/networkState';
import { session } from '../auth/session';

/** The production drain instance, wired to the real Dexie / push / upload deps. */
export const syncDrainService = new SyncDrainService({
  countPendingDelivery: () => inspectionRecordService.countPendingDelivery(),
  listEligibleForBackgroundSend: () =>
    inspectionRecordService.listEligibleForBackgroundSend(),
  getInspection: (externalId) => inspectionDB.inspections.get(externalId),
  push: (externalId) => inspectionPushService.push(externalId),
  sendPhotos: (externalId) => photoDeliveryService.send(externalId),
  isOnline: () => store.get(networkStatusAtom),
  // Offline is isOnline's call; a stored sign-in kept offline still counts.
  hasAuth: () => session.status() !== 'signedOut',
  now: () => Date.now(),
  wakeAfter: (delayMs, wake) => {
    const timer = setTimeout(wake, delayMs);
    return () => clearTimeout(timer);
  },
});

/** Delay before the startup drain, letting login/hydration settle first. */
const STARTUP_DRAIN_DELAY_MS = 8000;

let installed = false;

/**
 * Install the drain triggers (idempotent): startup, reconnect, session change and
 * app-foreground.
 * Every trigger funnels through drain()'s own guards (online, auth, cooldown, single
 * flight), so firing them liberally is safe.
 */
export function initSyncDrainTriggers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Startup: deliver anything left over from a previous session.
  setTimeout(() => {
    void syncDrainService.drain();
  }, STARTUP_DRAIN_DELAY_MS);

  // Reconnect: the moment the app knows it's back online.
  let wasOnline = store.get(networkStatusAtom);
  store.sub(networkStatusAtom, () => {
    const isOnline = store.get(networkStatusAtom);
    if (!wasOnline && isOnline) {
      void syncDrainService.drain();
    }
    wasOnline = isOnline;
  });

  // A pass skipped while signed out runs once the inspector signs in again. The session
  // also changes on sign-out and with connectivity, overlapping the reconnect trigger;
  // drain()'s guards make those extra calls harmless.
  session.onChange(() => {
    void syncDrainService.drain();
  });

  // App brought back to the foreground (the common field pattern: capture offline,
  // pocket the device, open it again in coverage).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void syncDrainService.drain();
    }
  });
}
